import { useCallback, useEffect, useId, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, type CaringChannel, type CaringEntry, type CaringUpdate } from '../api.ts';
import { answerRingingRequest, RingingRequestCard } from './InviteCard.tsx';

/**
 * How I'm told — a caregiver's own say over the alerts they get.
 *
 * The approval screen promises "You can change this later"; this is where
 * later is. For each person they help: which of the channels that person's
 * plan pays for, whether to hear about late and taken doses too, and quiet
 * hours. Not whether to hear about a missed dose. That is why they were
 * asked to help, and someone who no longer wants it stops helping instead,
 * in the open, where the family sees it.
 *
 * About the signed-in person, not the record on screen behind it, so it is
 * a dialog from the header (like the statement). The People tab opens it
 * with `focus` on that record, which puts that person first.
 *
 * "Ring {patient}'s reminders on my phone" only turns it OFF here (the
 * owner's decision of 3 Oct 2026: ringing needs both sides). Where it does
 * not ring, the card shows the family's question to accept or decline, or
 * who to ask.
 */

/** The same words the approval screen used, so the choice reads as the same one. */
const CHANNEL_LABEL: Record<CaringChannel, string> = {
  push: 'App notification',
  whatsapp: 'WhatsApp from DrBell',
  sms: 'SMS from DrBell',
  email: 'Email from DrBell',
};

/** Offered when quiet hours are first turned on: a night, on their own clock. */
const NIGHT = { from: '22:00', to: '07:00' };

export function Caring({
  focus, onBack, onLeft,
}: {
  /** A record to put first: the one the People tab was showing. */
  focus?: string;
  onBack: () => void;
  /**
   * They stopped helping someone. The session's list of records is stale, and
   * `remaining` says whether there is anyone left to show at all.
   */
  onLeft: (patientId: string, remaining: number) => void;
}) {
  const [caring, setCaring] = useState<CaringEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<{ caring: CaringEntry[] }>('/api/me/caring');
    /*
     * A `self` grant is a person's own phone on their own record: it gets
     * reminders, not a caregiver's alerts, and "stop helping yourself" is
     * not a sentence anyone should be offered.
     */
    const list = r.caring.filter((c) => c.role !== 'self');
    setCaring(list);
    return list;
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [load]);

  const stop = (entry: CaringEntry) =>
    setPending({
      title: `Stop helping ${entry.patientName}?`,
      body: `You will no longer see ${entry.patientName}’s medicines or be told about their doses. Their family will see that you stopped.`,
      confirmLabel: 'Stop helping',
      onConfirm: async () => {
        // Thrown on failure, so the dialog shows why and stays open.
        await api.del(`/api/me/caring/${entry.grantId}`);
        const list = await load().catch(() => (caring ?? []).filter((c) => c.grantId !== entry.grantId));
        setCaring(list);
        onLeft(entry.patientId, list.length);
      },
    });

  const back = (
    <button onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
      ← Back
    </button>
  );

  const ordered = caring && focus
    ? [...caring.filter((c) => c.patientId === focus), ...caring.filter((c) => c.patientId !== focus)]
    : caring;

  return (
    <section className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center gap-2">{back}</div>

      <header>
        <h2 className="text-lg font-semibold">How I&rsquo;m told</h2>
        <p className="text-sm text-(--muted)">For each person you help: how DrBell reaches you, and about what.</p>
      </header>

      {error && <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500">{error}</p>}

      {!ordered && !error && <p className="text-(--muted)">Loading…</p>}

      {ordered && ordered.length === 0 && (
        <p className="rounded-xl border border-dashed border-(--line) p-6 text-center text-sm text-(--muted)">
          You do not help look after anyone yet.
        </p>
      )}

      {ordered?.map((entry) => (
        <CaringCard
          // Keyed on whether it rings and what was asked, so an answer —
          // which reloads the list — starts the card afresh from the server.
          key={`${entry.grantId}:${entry.ringsHere ? 1 : 0}:${entry.ringingRequest?.askedAt ?? 0}`}
          entry={entry}
          focused={entry.patientId === focus}
          onStop={() => stop(entry)}
          onReload={() => void load().catch(() => undefined)}
        />
      ))}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

/** What the form holds; compared with what was last saved to know if Save means anything. */
interface Draft {
  channels: CaringChannel[];
  late: boolean;
  taken: boolean;
  /** A child's record only (kid mode): confirm each dose the child reports. */
  childReport?: boolean;
  quietHours: { from: string; to: string } | null;
  /** The hands-on helper's switch; only where it is offered (`mayRing`). */
  ringsHere?: boolean;
}

function draftOf(entry: CaringEntry): Draft {
  return {
    channels: entry.channels, late: entry.alerts.late, taken: entry.alerts.taken, quietHours: entry.quietHours,
    // On by default for a new helper (the owner, 4 Oct 2026); an older server follows "taken".
    ...(entry.kid ? { childReport: entry.alerts.childReport ?? entry.alerts.taken } : {}),
    ...(entry.mayRing ? { ringsHere: entry.ringsHere === true } : {}),
  };
}

function same(a: Draft, b: Draft): boolean {
  return (
    a.channels.length === b.channels.length &&
    a.channels.every((c) => b.channels.includes(c)) &&
    a.late === b.late &&
    a.taken === b.taken &&
    a.childReport === b.childReport &&
    a.quietHours?.from === b.quietHours?.from &&
    a.quietHours?.to === b.quietHours?.to &&
    a.ringsHere === b.ringsHere
  );
}

function CaringCard({
  entry, focused, onStop, onReload,
}: {
  entry: CaringEntry;
  focused: boolean;
  onStop: () => void;
  /** A ringing request was answered: read the list again. */
  onReload: () => void;
}) {
  const id = useId();
  const name = entry.patientName;
  /** What the server holds now. Moved on after each save, so Save disables again. */
  const [saved, setSaved] = useState<Draft>(() => draftOf(entry));
  const [draft, setDraft] = useState<Draft>(() => draftOf(entry));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Anything changed after a save makes the "Saved" line a lie about the form.
  const change = (next: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...next }));
    setDone(false);
    setError(null);
  };

  const [answering, setAnswering] = useState(false);
  const answer = async (accept: boolean) => {
    setAnswering(true);
    setError(null);
    try {
      await answerRingingRequest(entry.grantId, accept);
      // A toast, because the reload that follows starts this card afresh.
      toast.success(accept ? `Accepted. ${name}’s reminders will ring on your phone.` : `Declined. ${name}’s family can see your answer.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) toast.info('This request has ended or was withdrawn.');
      else toast.error('Could not answer', e instanceof ApiError ? e.message : String(e));
    } finally {
      setAnswering(false);
      onReload();
    }
  };
  /*
   * Which of the three the card shows, from what the server last said (not
   * the draft): switched off and saved, the box goes, because turning it
   * back on is the family's question now, not this person's box.
   */
  const ringing = !entry.mayRing ? 'none' : saved.ringsHere ? 'switch' : entry.ringingRequest ? 'request' : 'ask';

  const noChannel = draft.channels.length === 0;
  const quietIncomplete = draft.quietHours !== null && (!draft.quietHours.from || !draft.quietHours.to);
  const dirty = !same(draft, saved);

  const save = async () => {
    // Refused here, not sent: the server would refuse it too, in fewer words.
    if (noChannel || quietIncomplete) return;
    setBusy(true);
    setError(null);
    try {
      const body: CaringUpdate = {
        channels: draft.channels,
        late: draft.late,
        taken: draft.taken,
        ...(draft.childReport !== undefined ? { childReport: draft.childReport } : {}),
        quietHours: draft.quietHours,
        ...(draft.ringsHere !== undefined ? { ringsHere: draft.ringsHere } : {}),
      };
      await api.put(`/api/me/caring/${entry.grantId}`, body);
      setSaved(draft);
      setDone(true);
    } catch (e) {
      // The plan changed under them (`not_in_plan`) or the grant ended: the
      // server says which in a sentence, so show its words.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const box = 'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-(--line) px-3';
  const field = 'min-h-11 w-full min-w-0 rounded-lg border border-(--line) bg-(--bg) px-3 text-sm';

  return (
    <article
      aria-labelledby={`${id}-name`}
      className={`space-y-5 rounded-xl border p-4 text-sm ${focused ? 'border-(--accent)' : 'border-(--line)'}`}
    >
      <h3 id={`${id}-name`} className="text-base font-semibold">{name}</h3>

      {/*
        The hands-on helper: whether this person's reminders ring on my phone.
        The alarm lives on the phone; turning it off is possible here too, so
        somebody who stops giving the medicines can say so from anywhere.
      */}
      {ringing === 'request' && (
        <RingingRequestCard entry={entry} busy={answering} onAnswer={(accept) => void answer(accept)} compact />
      )}
      {ringing === 'ask' && (
        <p className="text-xs text-(--muted)">
          {entry.askName
            ? `Ask ${entry.askName} if your phone should be the one that rings.`
            : `Ask ${name}’s family if your phone should be the one that rings.`}
        </p>
      )}
      {ringing === 'switch' && draft.ringsHere !== undefined && (
        <label className={box}>
          <input
            type="checkbox"
            checked={draft.ringsHere}
            disabled={busy}
            onChange={(e) => change({ ringsHere: e.target.checked })}
            aria-describedby={`${id}-rings`}
            className="h-4 w-4 shrink-0"
          />
          <span className="py-2">
            <span className="block font-medium">Ring {name}&rsquo;s reminders on my phone</span>
            <span id={`${id}-rings`} className="block text-xs text-(--muted)">
              For whoever gives the medicines. Taken and Later work from the alarm, and the family is still told
              about a missed dose.
            </span>
          </span>
        </label>
      )}

      {entry.caregiverAlerts === false && (
        <p className="rounded-lg border border-(--line) bg-(--panel) p-3">
          {name} is on the Free plan, so you are not told about doses. Your choices below are kept for when the
          family moves to Plus or Family.
        </p>
      )}

      <fieldset>
        <legend className="font-medium">How you will be told</legend>
        <div className="mt-2 space-y-1">
          {entry.offered.map((c) => (
            <label key={c} className={box}>
              <input
                type="checkbox"
                checked={draft.channels.includes(c)}
                disabled={busy}
                onChange={(e) =>
                  change({
                    channels: e.target.checked
                      ? entry.offered.filter((o) => o === c || draft.channels.includes(o))
                      : draft.channels.filter((x) => x !== c),
                  })
                }
                className="h-4 w-4 shrink-0"
              />
              {CHANNEL_LABEL[c]}
            </label>
          ))}
        </div>
        {/* On a plan with only the app, a lone box with no explanation reads
            as WhatsApp and SMS having been hidden from them. */}
        {entry.offered.length === 1 && entry.offered[0] === 'push' && (
          <p className="mt-1 text-xs text-(--muted)">Only what {name}&rsquo;s plan includes can be chosen.</p>
        )}
        {noChannel && (
          <p role="alert" className="mt-1 text-sm text-red-500">Keep at least one way to be told.</p>
        )}
      </fieldset>

      <fieldset>
        <legend className="font-medium">Tell me when</legend>
        <div className="mt-2 space-y-1">
          {/* Shown, ticked and fixed rather than left out: a list without it
              reads as though missed doses had been turned off. */}
          <label className={`${box} cursor-default`}>
            {/* As the server says: on for everyone asked to help; for a
                viewer, as invited — usually off, and not theirs to change. */}
            <input type="checkbox" checked={entry.alerts.missed} disabled aria-describedby={`${id}-missed`} className="h-4 w-4 shrink-0" />
            <span className="py-2">
              <span className="block">A dose is missed</span>
              <span id={`${id}-missed`} className="block text-xs text-(--muted)">
                {entry.alerts.missed
                  ? 'Always on: this is why you were asked to help.'
                  : 'You were invited to see doses, not to be told about missed ones.'}
              </span>
            </span>
          </label>
          <label className={box}>
            <input
              type="checkbox"
              checked={draft.late}
              disabled={busy}
              onChange={(e) => change({ late: e.target.checked })}
              className="h-4 w-4 shrink-0"
            />
            A dose is late
          </label>
          <label className={box}>
            <input
              type="checkbox"
              checked={draft.taken}
              disabled={busy}
              onChange={(e) => change({ taken: e.target.checked })}
              className="h-4 w-4 shrink-0"
            />
            A dose is taken
          </label>
          {/* A child's record: asked to confirm each dose the child reports — on unless turned off. */}
          {draft.childReport !== undefined && (
            <label className={box}>
              <input
                type="checkbox"
                checked={draft.childReport}
                disabled={busy}
                onChange={(e) => change({ childReport: e.target.checked })}
                className="h-4 w-4 shrink-0"
              />
              <span className="py-2">
                <span className="block">{name} reports a dose</span>
                <span className="block text-xs text-(--muted)">
                  So you can confirm it. On a child&rsquo;s record, a grown-up confirms each dose the child reports.
                </span>
              </span>
            </label>
          )}
        </div>
      </fieldset>

      <fieldset>
        <legend className="font-medium">Quiet hours</legend>
        <div className="mt-2 space-y-1">
          <label className={box}>
            <input
              type="radio"
              name={`${id}-quiet`}
              checked={draft.quietHours === null}
              disabled={busy}
              onChange={() => change({ quietHours: null })}
              className="h-4 w-4 shrink-0"
            />
            No quiet hours
          </label>
          <label className={box}>
            <input
              type="radio"
              name={`${id}-quiet`}
              checked={draft.quietHours !== null}
              disabled={busy}
              // Back to what was saved if there was a window, else a night.
              onChange={() => change({ quietHours: saved.quietHours ?? NIGHT })}
              className="h-4 w-4 shrink-0"
            />
            Quiet hours
          </label>
        </div>
        {draft.quietHours && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="min-w-0">
              <span className="block text-xs text-(--muted)">From</span>
              <input
                type="time"
                value={draft.quietHours.from}
                disabled={busy}
                onChange={(e) => change({ quietHours: { ...draft.quietHours!, from: e.target.value } })}
                className={field}
              />
            </label>
            <label className="min-w-0">
              <span className="block text-xs text-(--muted)">Until</span>
              <input
                type="time"
                value={draft.quietHours.to}
                disabled={busy}
                onChange={(e) => change({ quietHours: { ...draft.quietHours!, to: e.target.value } })}
                className={field}
              />
            </label>
          </div>
        )}
        <p className="mt-1 text-xs text-(--muted)">
          Late and taken alerts wait until morning. A missed dose still comes through.
        </p>
      </fieldset>

      {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>}
      <p aria-live="polite" className="text-sm">
        {done && !dirty ? `Saved. ${name}’s family can see the change.` : ''}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled={!dirty || busy || noChannel || quietIncomplete}
          onClick={() => void save()}
          className="min-h-11 rounded-lg bg-(--accent) px-5 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          disabled={busy}
          onClick={onStop}
          className="ml-auto min-h-11 rounded-lg border border-red-500/50 px-3 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-40"
        >
          Stop helping {name}
        </button>
      </div>
    </article>
  );
}
